'use client';

import { c as _c } from "react/compiler-runtime";
import { parseAsBoolean, useQueryState } from 'nuqs';
import type { ComponentType } from 'react';
import { Suspense } from 'react';
import type { PrV2FileTreeOpenBreakpoint } from '../../file-tree-v2/pr-v2-file-tree-utils';
import { clampFileTreeWidth, PR_V2_FILE_TREE_WIDTH } from '../../file-tree-v2/pr-v2-file-tree-utils';
import { Skeleton } from '@/components/ui/skeleton';
import { usePrV2ViewPreferencesStore } from '@/lib/stores/pr-v2-view-preferences-store';
import { useZenModeStore } from '@/lib/stores/zen-mode-store';
import { cn } from '@/lib/utils';
const FILE_TREE_ROWS = [1, 2, 3, 4, 5];

// Whole class names, so Tailwind generates both.
const SHOW_FROM_BREAKPOINT_CLASS: Record<PrV2FileTreeOpenBreakpoint, string> = {
  'xl': 'xl:block',
  '2xl': '2xl:block'
};
type FileTreePlaceholderProps = {
  width?: number;
  isCollapsed?: boolean | null;
  openFrom?: PrV2FileTreeOpenBreakpoint;
};
function FileTreePlaceholder(t0) {
  const $ = _c(8);
  const {
    width: t1,
    isCollapsed: t2,
    openFrom: t3
  } = t0;
  const width = t1 === undefined ? PR_V2_FILE_TREE_WIDTH : t1;
  const isCollapsed = t2 === undefined ? null : t2;
  const openFrom = t3 === undefined ? "xl" : t3;
  if (isCollapsed === true) {
    return null;
  }
  const t4 = isCollapsed === false ? "md:block" : SHOW_FROM_BREAKPOINT_CLASS[openFrom];
  let t5;
  if ($[0] !== t4) {
    t5 = cn("sticky top-9 hidden h-[calc(100vh-96px)] shrink-0 bg-zinc-950 p-2", t4);
    $[0] = t4;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  let t6;
  if ($[2] !== width) {
    t6 = {
      width
    };
    $[2] = width;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  let t7;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="space-y-1">{FILE_TREE_ROWS.map(_temp)}</div>;
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  let t8;
  if ($[5] !== t5 || $[6] !== t6) {
    t8 = <aside aria-hidden="true" data-testid="pr-v2-file-tree-skeleton" className={t5} style={t6}>{t7}</aside>;
    $[5] = t5;
    $[6] = t6;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  return t8;
}
function _temp(row) {
  return <Skeleton key={row} className="h-5 w-full" />;
}
function FileTreeToolbarPlaceholder(t0) {
  const $ = _c(8);
  const {
    width: t1,
    isCollapsed: t2
  } = t0;
  const width = t1 === undefined ? PR_V2_FILE_TREE_WIDTH : t1;
  const isCollapsed = t2 === undefined ? null : t2;
  let t3;
  if ($[0] !== isCollapsed) {
    t3 = isCollapsed !== false ? <div aria-hidden="true" data-testid="pr-v2-file-tree-compact-toolbar-skeleton" className={cn("hidden h-9 w-max shrink-0 items-center justify-end gap-1 bg-zinc-950 pr-2 pl-1 md:flex", isCollapsed === null && "xl:hidden")}><Skeleton className="ml-auto h-6 w-16 rounded-md" /><Skeleton className="size-6 shrink-0 rounded-md" /></div> : null;
    $[0] = isCollapsed;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== isCollapsed || $[3] !== width) {
    t4 = isCollapsed !== true ? <div aria-hidden="true" data-testid="pr-v2-file-tree-toolbar-skeleton" className={cn("hidden shrink-0 bg-zinc-950 px-2", isCollapsed === false ? "md:block" : "xl:block")} style={{
      width
    }}><div className="flex h-9 items-center gap-1"><Skeleton className="h-6 w-16 rounded-md" /><Skeleton className="ml-auto h-6 w-6 rounded-md" /><Skeleton className="h-6 w-6 rounded-md" /></div></div> : null;
    $[2] = isCollapsed;
    $[3] = width;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t3 || $[6] !== t4) {
    t5 = <>{t3}{t4}</>;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
}
function FileTreePlaceholderWithPreferences(t0) {
  const $ = _c(7);
  const {
    placeholder: Placeholder,
    openFrom
  } = t0;
  const [isCollapsed] = useQueryState("v2FileTreeCollapsed", parseAsBoolean);
  const isZenMode = useZenModeStore(_temp2);
  const width = usePrV2ViewPreferencesStore(_temp3);
  if (isZenMode) {
    return null;
  }
  let t1;
  if ($[0] !== width) {
    t1 = clampFileTreeWidth(width);
    $[0] = width;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== Placeholder || $[3] !== isCollapsed || $[4] !== openFrom || $[5] !== t1) {
    t2 = <Placeholder width={t1} isCollapsed={isCollapsed} openFrom={openFrom} />;
    $[2] = Placeholder;
    $[3] = isCollapsed;
    $[4] = openFrom;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
function _temp3(state_0) {
  return state_0.fileTreeWidthPx;
}
function _temp2(state) {
  return state.isZenMode;
}
export function PrV2FileTreeSkeleton(t0) {
  const $ = _c(4);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    openFrom: t2
  } = t1;
  const openFrom = t2 === undefined ? "xl" : t2;
  let t3;
  if ($[2] !== openFrom) {
    t3 = <Suspense fallback={<FileTreePlaceholder openFrom={openFrom} />}><FileTreePlaceholderWithPreferences placeholder={FileTreePlaceholder} openFrom={openFrom} /></Suspense>;
    $[2] = openFrom;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}
export function PrV2FileTreeToolbarSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Suspense fallback={<FileTreeToolbarPlaceholder />}><FileTreePlaceholderWithPreferences placeholder={FileTreeToolbarPlaceholder} /></Suspense>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}